import moment from "moment";
import { getScheduleTimezoneOffsetHours, toLocalDayKey } from "./reportScheduleMapper";

const normalizeRun = (run) => ({
  ran_at: run?.ran_at || run?.created_at || null,
  status: run?.status || "unknown",
  isUpcoming: !!run?.isUpcoming,
  message: run?.message || run?.last_run_message || run?.error || "",
  upload_file_id: run?.upload_file_id || run?.uploadFileId || null,
  file_name: run?.file_name || run?.export_file_name || "",
  row_count: run?.row_count ?? run?.rows ?? null,
  trigger: run?.trigger || run?.trigger_type || "",
  sftp_status: run?.sftp_status || run?.sftp_success,
  schedule_id: run?.schedule_id || null,
  schedule_name: run?.schedule_name || run?.name || "",
  report_id: run?.report_id || null,
  report_name: run?.report_name || "",
  next_run_at: run?.next_run_at || null,
});

export const normalizeScheduleRuns = (runs = []) =>
  (Array.isArray(runs) ? runs : [])
    .map(normalizeRun)
    .filter((run) => !!run.ran_at);

export const mergeScheduleRuns = (...runLists) => {
  const seen = new Set();
  const merged = [];

  runLists.flat().forEach((run) => {
    const normalized = normalizeRun(run);
    if (!normalized.ran_at) return;
    const key = `${normalized.ran_at}|${normalized.status}|${normalized.upload_file_id || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(normalized);
  });

  return merged.sort((a, b) => new Date(b.ran_at) - new Date(a.ran_at));
};

export const groupRunsByDayKey = (runs = [], timezone = "GMT-8") => {
  const offsetHours = getScheduleTimezoneOffsetHours(timezone);
  const grouped = {};

  normalizeScheduleRuns(runs).forEach((run) => {
    const dayKey = toLocalDayKey(run.ran_at, offsetHours);
    if (!grouped[dayKey]) grouped[dayKey] = [];
    grouped[dayKey].push(run);
  });

  Object.values(grouped).forEach((dayRuns) => {
    dayRuns.sort((a, b) => new Date(b.ran_at) - new Date(a.ran_at));
  });

  return grouped;
};

export const filterRunsForMonth = (runs = [], year, month, timezone = "GMT-8") => {
  const offsetHours = getScheduleTimezoneOffsetHours(timezone);
  const monthKey = `${year}-${String(month).padStart(2, "0")}`;

  return normalizeScheduleRuns(runs).filter((run) => {
    const dayKey = toLocalDayKey(run.ran_at, offsetHours);
    return dayKey.startsWith(monthKey);
  });
};

export const buildMonthCalendarDays = (year, month) => {
  const firstOfMonth = moment({ year, month: month - 1, day: 1 });
  const start = firstOfMonth.clone().startOf("week");
  const end = firstOfMonth.clone().endOf("month").endOf("week");
  const days = [];
  const cursor = start.clone();

  while (cursor.isSameOrBefore(end, "day")) {
    days.push({
      date: cursor.clone(),
      dayKey: cursor.format("YYYY-MM-DD"),
      inCurrentMonth: cursor.month() === firstOfMonth.month(),
    });
    cursor.add(1, "day");
  }

  return days;
};

export const isPastOrToday = (dayKey, timezone = "GMT-8") => {
  const offsetHours = getScheduleTimezoneOffsetHours(timezone);
  const todayKey = toLocalDayKey(new Date(), offsetHours);
  return dayKey <= todayKey;
};

export const isFutureDay = (dayKey, timezone = "GMT-8") => {
  const offsetHours = getScheduleTimezoneOffsetHours(timezone);
  const todayKey = toLocalDayKey(new Date(), offsetHours);
  return dayKey > todayKey;
};

export const buildUpcomingRunsFromEntries = (
  entries = [],
  timezone = "GMT-8",
  year,
  month
) => {
  const offsetHours = getScheduleTimezoneOffsetHours(timezone);
  const monthKey = `${year}-${String(month).padStart(2, "0")}`;
  const now = Date.now();

  return (Array.isArray(entries) ? entries : [])
    .filter((entry) => entry?.next_run_at)
    .map((entry) => {
      const nextRunAt = entry.next_run_at;
      const dayKey = toLocalDayKey(nextRunAt, offsetHours);
      return {
        ran_at: nextRunAt,
        status: "scheduled",
        isUpcoming: true,
        message: "",
        upload_file_id: null,
        file_name: "",
        row_count: null,
        trigger: entry.trigger || entry.trigger_type || "",
        schedule_id: entry.schedule_id || entry.id || null,
        schedule_name: entry.schedule_name || entry.name || "",
        report_id: entry.report_id || null,
        report_name: entry.report_name || "",
        next_run_at: nextRunAt,
        dayKey,
      };
    })
    .filter((run) => {
      if (!run.dayKey.startsWith(monthKey)) return false;
      const runTime = new Date(run.ran_at).getTime();
      return Number.isFinite(runTime) && runTime > now;
    });
};

export const groupUpcomingByDayKey = (upcomingRuns = [], timezone = "GMT-8") => {
  const offsetHours = getScheduleTimezoneOffsetHours(timezone);
  const grouped = {};

  (Array.isArray(upcomingRuns) ? upcomingRuns : []).forEach((run) => {
    const dayKey = run.dayKey || toLocalDayKey(run.ran_at, offsetHours);
    if (!grouped[dayKey]) grouped[dayKey] = [];
    grouped[dayKey].push(run);
  });

  Object.values(grouped).forEach((dayRuns) => {
    dayRuns.sort((a, b) => new Date(a.ran_at) - new Date(b.ran_at));
  });

  return grouped;
};

export const getDayStatusSummary = (runs = []) => {
  if (!runs.length) return null;
  if (runs.some((run) => run.status === "failed")) return "failed";
  if (runs.some((run) => run.status === "success")) return "success";
  return "skipped";
};

const getRunReportLabel = (run) =>
  run.report_name?.trim() ||
  (run.report_id ? `Report #${run.report_id}` : "") ||
  run.schedule_name?.trim() ||
  (run.schedule_id ? `Schedule #${run.schedule_id}` : "");

export const getUniqueReportLabels = (runs = []) => {
  const labels = [];
  const seen = new Set();

  (Array.isArray(runs) ? runs : []).forEach((entry) => {
    const run = entry?.isUpcoming ? entry : normalizeRun(entry);
    if (!run.ran_at) return;

    const label = getRunReportLabel(run);
    if (!label || seen.has(label)) return;
    seen.add(label);
    labels.push({
      key: String(run.report_id || run.schedule_id || label),
      label,
      reportId: run.report_id || null,
    });
  });

  return labels;
};

export const groupRunsByReport = (runs = []) => {
  const groups = new Map();

  (Array.isArray(runs) ? runs : []).forEach((entry) => {
    const run = entry?.isUpcoming ? entry : normalizeRun(entry);
    if (!run.ran_at) return;
    const reportKey = run.report_id || run.report_name || run.schedule_id || "unknown";
    const reportLabel = getRunReportLabel(run) || "Export";

    if (!groups.has(reportKey)) {
      groups.set(reportKey, {
        key: String(reportKey),
        reportId: run.report_id || null,
        reportLabel,
        scheduleName: run.schedule_name || "",
        runs: [],
      });
    }
    groups.get(reportKey).runs.push(run);
  });

  return Array.from(groups.values());
};
